import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faRedo, faTrashRestore } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import { Size, useSize } from 'react-hook-size';
import nanoid from "nanoid"; 
import _ from "lodash";
import moment from 'moment';
import { Spinner } from "react-bootstrap";

import { Source, Layer, NavigationControl } from 'react-map-gl';
import MapGL from 'react-map-gl';
import { ViewState } from 'react-map-gl-draw/node_modules/@types/react-map-gl';
import { DrawPolygonMode, EditingMode, Editor } from 'react-map-gl-draw';

import ESRI_BASE from '../../../components/Map/styles/esri-base.json';
import ESRI_BASE_SATELLITE from '../../../components/Map/styles/esri-base-satellite.json';
import { MAPBOX_BASE_SATELLITE } from '../../../components/Map/styles/mapbox-base-satellite';
import ESRI_BASE_LABELS from '../../../components/Map/styles/esri-base-labels.json';
import NAIP from '../../../components/Map/styles/naip.json';
import { editFieldsLayer, clusterLayer, fieldsBorderLayer, fieldsBorderLayerSelected, zonesBorderLayerEdit, fieldsHighlightLayer, fieldsLayer, zonesLayer, zonesPrelimBorderLayer, zonesPrelimLayer, fieldsSelectedBorderLayer, unclusteredLayer } from '../../../components/Map/styles/fields';
import { getEditHandleStyle, getFeatureStyle } from '../../../components/Map/styles/draw';

import EditZoneForm from './EditZoneForm';
import { Field, Zone, LayerVisibility, DataSourceLayerMeta, DataSourceRasterLayer} from '../../../types/types';
import { GET_FIELDS } from '../../../../../queries';
import { GetFields } from '../../../../../__generated__/types';
import { useRasterDataSources } from '../../../hooks/useRasterDataSources';

import { createFeatureCollection, createFeatureFromField, createPolygonFeatureFromField, getViewport, zoomToFeature } from '../../../utils/mapUtils';
import { generatePath } from 'react-router-dom';
import LayerVisibilityControlPanel from './LayerVisibilityControlPanel';
import { 
    HIGH_RES_COLOR,
    PGI_DATA_SOURCE, 
    VVIC_DATA_SOURCE,
    CCCI_DATA_SOURCE,
    TOANDRE_DATA_SOURCE,
} from '../../../constants';
import ZoneInfoDrawer from './ZoneInfoDrawer';
import { useStore } from '../../../providers/AppProvider';
import { getTileUrlTemplate } from "../../../utils/dataSourceUtils";
import { createCentroidFeatureFromGeom } from '../../../utils/mapUtils';
import LandscanService from '../../../api/landscanService';
import { LoadingSpinnerSmallTextInside } from '../../../../../components/LoadingSpinnerSmallTextInside';

const navControlStyle = {
    left: 10,
    top: 10
};

const initLayerVisibility: LayerVisibility[] = [{
    layer_id: 'naip',
    layer_name: 'Historical Imagery',
    is_visible: false,
    order: 3,
    source: NAIP,
},{
    layer_id: 'MAPBOX',
    layer_name: 'Mapbox Imagery',
    is_visible: true,
    order: 4,
    source: MAPBOX_BASE_SATELLITE,
},{
    layer_id: 'ESRI_LABELS',
    layer_name: 'Labels',
    is_visible: true,
    order: 1,
    source: ESRI_BASE_LABELS,
}];

const FIELD_APP_URL = process.env.REACT_APP_FIELD_MGR_ROOT_URL;

export const EditZone = () => {
    const history = useHistory();

    const mapContainerRef = useRef(null);
    const editorRef = useRef(null);

    const { vp, zone, field} = useParams();

    const { width, height } = useSize(mapContainerRef);

    const [viewport, setViewport] = useState<ViewState>(getViewport(vp));

    const [fieldsDataSource, setFieldsDataSource] = useState(null);
    const [fieldsDataSourceSelected, setFieldsDataSourceSelected] = useState(null);
    const [editFeature, setEditFeature] = useState<any>(null);
    const [mode, setMode] = useState<DrawPolygonMode | EditingMode>(new DrawPolygonMode());
    const [, setSelectedFeatureIndex] = useState(null);
    const [editField, setEditField] = useState<Field | null>(null);
    const [fields, setFields] = useState<Field[]>([]);
    const [zones, setZones] = useState<Zone[] | null>(null);
    const [zonesPrelim, setZonesPrelim] = useState<Zone[] | null>(null);
    const [selectedZoneset, setSelectedZoneset] = useState<number>(0);
    const [updateZonesets, setUpdateZonesets] = useState<number>(0);
    const [recentPublishedZoneset, setRecentPublishedZoneset] = useState<string>("");
    const [selectedZonesetStatus, setSelectedZonesetStatus] = useState<string>("published");
    const [publishingZone, setPublishingZone] = useState<number>(-99);
    const [unPublishingZone, setUnPublishingZone] = useState<number>(-99);
    const [jobStatus, setJobStatus] = useState<string>("no jobs running");
    const [jobRunning, setJobRunning] = useState<string>("");
    const [jobPercent, setJobPercent] = useState<number>(0);
    const [updateToNewPublishedSet, setUpdateToNewPublishedSet] = useState<boolean>(false);
    const [updateToNewPrelimSet, setUpdateToNewPrelimSet] = useState<boolean>(false);

    // Compute data sources synchronously during render so the map always reflects
    // the current selectedZoneset + selectedZonesetStatus without waiting for an effect cycle.
    const zonesDataSource = useMemo(() => {
        if (!zones?.[selectedZoneset]?.geojson?.features) return null;
        return createFeatureCollection(zones[selectedZoneset].geojson.features.map((f: any) => f));
    }, [zones, selectedZoneset]);

    const zonesPointDataSource = useMemo(() => {
        if (!zones?.[selectedZoneset]?.geojson?.features) return null;
        return createFeatureCollection(
            zones[selectedZoneset].geojson.features.map((f: any) => {
                const feat = createCentroidFeatureFromGeom(f.geometry, f.properties);
                feat.properties = { ...f.properties };
                return feat;
            })
        );
    }, [zones, selectedZoneset]);

    // Avoid mutating the raw feature objects from zonesPrelim state — use spread to copy.
    const zonesPrelimDataSource = useMemo(() => {
        if (!zonesPrelim?.[selectedZoneset]?.geojson?.features) return null;
        return createFeatureCollection(
            zonesPrelim[selectedZoneset].geojson.features.map((f: any) => ({
                ...f,
                properties: { ...f.properties, label: f.properties.value },
            }))
        );
    }, [zonesPrelim, selectedZoneset]);

    const zonesPrelimPointDataSource = useMemo(() => {
        if (!zonesPrelim?.[selectedZoneset]?.geojson?.features) return null;
        return createFeatureCollection(
            zonesPrelim[selectedZoneset].geojson.features.map((f: any) => {
                const feat = createCentroidFeatureFromGeom(f.geometry, f.properties);
                feat.properties = { ...f.properties, label: f.properties.value };
                return feat;
            })
        );
    }, [zonesPrelim, selectedZoneset]);

    const [overlayVisibilityMapControl, setOverlayVisibleMapControl] = useState<LayerVisibility[]>([]);
    const [dateSelectorParameters, setDateSelectorParameters] = useState<any>(null);
    const [dateIndexSelected, setDateIndexSelected] = useState<Array<number>>([0,0,0]);
    const [loadedTiles, setLoadedTiles] = useState<boolean>(true);
    const [timelineStatus, setTimelineStatus] = useState<string>("");
    const [activeAnimation, setActiveAnimation] = useState<string>("not engaged"); // "not engaged" | "playing" | "paused"
    const [indexA, setIndexA] = useState<number>(0);
    const [datesAvailable, setDatesAvailable] = useState<Array<string>>([])
    const [dateSelected, setDateSelected] = useState<string | null>(null);
    const [animationRasterDataSources, setAnimationRasterDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [map, setMap] = useState<any | null>(null);
    const [animationInterval, setAnimationInterval] = useState<any>(undefined);
    const [timerInterval, setTimerInterval] = useState<number>(0);

    const [selectedZoneName, setSelectedZoneName] = useState<string | null>(null);
    const [overlayVisibility, setOverlayVisible] = useState<LayerVisibility[]>(initLayerVisibility);

    const {data: fieldData} = useQuery<GetFields>(GET_FIELDS);

    const [rasterDataSources, setRasterDataSources] = useState<DataSourceRasterLayer[]>([]);


    const { orgId, socket, updateFieldSelected, updateLayersForExport, setDownloadDateRange, settings, state} = useStore();
    const activeZoneset = selectedZonesetStatus === "published"
        ? zones?.[selectedZoneset]
        : zonesPrelim?.[selectedZoneset];
    const activeZoneSourceKey = `${selectedZonesetStatus}-${activeZoneset?.zonesetId ?? selectedZoneset}`;
    const activeZonePolygonSourceId = `zonesDataSourceID-${selectedZonesetStatus}`;
    const activeZonePointSourceId = `zonesPointDataSourceID-${selectedZonesetStatus}`;
    const activeZoneFillLayerId = `zones-fill-${selectedZonesetStatus}`;
    const activeZoneBorderLayerId = `zones-polygon-bnd-${selectedZonesetStatus}`;
    const activeZoneCircleLayerId = `zones-circle-${selectedZonesetStatus}`;
    const activeZoneLabelLayerId = `zones-label-${selectedZonesetStatus}`;
    let orgIdImages = "fd8ce854-9f8e-11ea-a22c-02a6f2671be5"
    const RASTER_DATA_SOURCES: DataSourceLayerMeta[] = [{
        id: `${orgId}${HIGH_RES_COLOR}`,
        disambiguate:'High Res. Normal Color',
        name:'UAS Imagery',
        type: 'base',
        order: 2,
        visible: true,
        minZoom: 0,
        maxZoom: 24
    },{
        id: `${orgIdImages}${VVIC_DATA_SOURCE}`,
        type: 'overlay',
        order: 1,
        visible: false,
        minZoom: 10,
        maxZoom: 24
    },{
        id: `${orgIdImages}${PGI_DATA_SOURCE}`,
        type: 'overlay',
        order: 2,
        visible: false,
        minZoom: 10,
        maxZoom: 24
    },{
        id: `${orgIdImages}${TOANDRE_DATA_SOURCE}`,
        type: 'overlay',
        order: 3,
        visible: false,
        minZoom: 10,
        maxZoom: 24
    }/*,{
        id: `${orgIdImages}${CCCI_DATA_SOURCE}`,
        type: 'overlay',
        order: 4,
        visible: false,
        minZoom: 10,
        maxZoom: 24
    }*/
];
    const { dataSources, loading } = useRasterDataSources(RASTER_DATA_SOURCES);


    //  BEGIN DATE / TIMELINE STUFF  
    //
    useEffect(() => {
        console.log("A041524 rasterDataSources",rasterDataSources);
        console.log("A041524 animationRasterDataSources", animationRasterDataSources);
        console.log("A041524 overlayVisibility", overlayVisibility);
    }, [animationRasterDataSources, rasterDataSources, overlayVisibility]);

    useEffect(() => {
        console.log("041224 overlayVisibilityMapControl", overlayVisibilityMapControl);
    }, [overlayVisibilityMapControl]);

    useEffect(() => {
        if (!loadedTiles){
            assessAnimationLoad();
        }
    }, [loadedTiles]);

    useEffect(() => {
        console.log("B032724 dateSelectorParameters", dateSelectorParameters);
        console.log("B032724 dateIndexSelected", dateIndexSelected);
        console.log("B032724 datesAvailable", datesAvailable);
        console.log("B032724 indexA", indexA);
        console.log("B032724 dateSelectorParameters.marks[dateIndexSelected[1]].props.children", dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children);
        setDateSelected(dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children ? `${settings.season_year}-${dateSelectorParameters?.marks[dateIndexSelected[1]]?.props.children}` : null);
    }, [dateIndexSelected, dateSelectorParameters, datesAvailable, indexA]);

    const loadAnimationTiles = (_dateSelectorParameters:any) => {
        let _datesAvailable:Array<string> = [];
        let _sequence = _dateSelectorParameters.sequence;

        for (let s in _sequence){
            if ((parseInt(_sequence[s][0]) >= dateIndexSelected[0]) && (parseInt(_sequence[s][0]) <= dateIndexSelected[2])){
                _datesAvailable.push(s)
            }
        };
        setDatesAvailable(_datesAvailable);
        let visibleRasterDataSources = rasterDataSources.filter((d: DataSourceRasterLayer) => {
            let ds = overlayVisibilityMapControl.find((item) => { return item.layer_id === d.id });
            return (ds?.is_visible == true);
        });
        let tempArray:any = [];
        visibleRasterDataSources.forEach((sr: DataSourceRasterLayer, index:number) => {
            let c = generateTileURLsAtDates(sr, _datesAvailable)
            tempArray = tempArray.concat(c);
        })
        setAnimationRasterDataSources(tempArray);
        setLoadedTiles(false);
    };

    useEffect(() => {
        if (dateSelectorParameters && dateIndexSelected){
            console.log("083123 dateSelectorParameters", dateSelectorParameters);
            console.log("083123 dateIndexSelected", dateIndexSelected);
            let _sequence = dateSelectorParameters.sequence;
            let min_date = dateSelectorParameters.min_date;
            let max_date = dateSelectorParameters.max_date;
            for (let s in _sequence){
                if (parseInt(_sequence[s][0]) == dateIndexSelected[0]){
                    min_date = s;
                } 
                if (parseInt(_sequence[s][0]) == dateIndexSelected[2]){
                    max_date = s;
                } 
            };
            let momentDateMin = moment(min_date).format('YYYY-MM-DD');
            var minDate:any = new Date(momentDateMin);
            var minDaysSinceEpoch = Math.floor(minDate/8.64e7);
            let momentDateMax = moment(max_date).format('YYYY-MM-DD');
            var maxDate:any = new Date(momentDateMax);
            var maxDaysSinceEpoch = Math.floor(maxDate/8.64e7);
            setDownloadDateRange({
                "yearStart": minDaysSinceEpoch,
                "min_date": min_date,
                "yearEnd": maxDaysSinceEpoch,
                "max_date": max_date,
            });
        }
        
    }, [dateSelectorParameters, dateIndexSelected]);

    function assessAnimationLoad(){
        if (map){
            if (map.loaded() == true && map.areTilesLoaded() == true) {
                console.log("load is complete");
                setLoadedTiles(true);
                playAnimation(datesAvailable);
            } else {
                console.log("load is incomplete");
                setTimeout(function() {
                    assessAnimationLoad();
                },500);
            }
        }
    }

    useEffect(() => {
        if (activeAnimation === "playing"){
            //console.log("indexA", indexA);
            //console.log("092022 datesAvailable", datesAvailable);
            let s = datesAvailable[indexA];
            let x = animationRasterDataSources.map((source: DataSourceRasterLayer, sourceIndex:number) => {
                if (source.dateString === s){
                    source.opacity = 1;
                }else{
                    source.opacity = 0;
                }
                return source;
            })
            setTimelineStatus(s);
            setAnimationRasterDataSources(x);
            if (indexA + 1 == datesAvailable.length){
                setActiveAnimationFromParent("paused");
                clearInterval(animationInterval);
            }else{
                setIndexA(prev => prev + 1 )
            }
        }else{
            clearInterval(animationInterval);
        }
    }, [timerInterval]);

    function setActiveAnimationFromParent(_settingToApply:string){
        setActiveAnimation(_settingToApply)
    }

    function playAnimation(_datesAvailable:Array<string>){
        //console.log("_datesAvailable",_datesAvailable);
        //console.log("datesAvailable",datesAvailable);
        //console.log("animationRasterDataSources",animationRasterDataSources);
        if (animationInterval){
            clearInterval(animationInterval);
        }
        //console.log("indexA + 1 == datesAvailable.length", indexA + 1, datesAvailable.length);
        if (indexA + 1 >= datesAvailable.length){
            //console.log("setIndexA(0);");
            setIndexA(0);
        }
        setAnimationInterval(setInterval(myTimer, 1000));

        function myTimer() {
            setTimerInterval(prev => prev + 1 )
        }
    }
    const generateTileURLsAtDates = (_rasterDataSourceToAnimate:DataSourceRasterLayer, _datesAvailable:Array<string>) => {
        const keyFramedRasterDataSourceToAnimate = _datesAvailable.map((dateString: string, index:number) => { 
            let _tempDS = {..._rasterDataSourceToAnimate};
            _tempDS["opacity"] = 0.0;
            _tempDS["dateString"] = dateString;
            _tempDS.id = `${_tempDS.id}_${index}`;
            _tempDS.uuid = `${_tempDS.uuid}_${index}`;
            _tempDS.tiles = getTileUrlTemplate(
                _tempDS.source, // 1
                _tempDS.source.stac_address!, // 2 
                _tempDS.source.collection_id!, // 3
                'footprint_id', // 4
                _tempDS.source.min_date, // 5
                dateString, // 6
                _tempDS.source.date_display_resolution!, // 7
                "template-singledate-p1", // 8
                null, // 9 
                "global", // 10
                settings.season_year // 11
            )
            return _tempDS;
        })
        return keyFramedRasterDataSourceToAnimate;
    };
    useEffect(() => {
        if (!loading && dataSources) {
            console.log("C041224 dataSources", dataSources)
            const overlaySources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => {
                return (d.type == "overlay" && d.name !== "Brightness Index");
            })
            const baseSources: DataSourceRasterLayer[] = dataSources.filter((d: DataSourceRasterLayer) => d.type == "base")
            const overlayLayers: LayerVisibility[] = overlaySources.map((d: DataSourceRasterLayer) => {
                console.log("051524 d", d);
                let _name = d.name;
                if (_name === "Productivity"){
                    _name = "Biomass";
                }
                if (_name === "Canopy Chlorophyll Concentration Index"){
                    _name = "CCCI";
                }
                if (_name === "Top of atmosphere NDRE"){
                    _name = "NDRE";
                }
                return {
                    layer_id: d.id,
                    layer_name: _name,
                    is_visible: d.is_visible, //d.name === "Productivity" ? true : d.is_visible,
                    order: d.order,
                    source: d.source,
                    override_max_min: (_name === "Stress" || _name === "Biomass") ? [0,100] : null,
                };
            });
            const baseLayers: LayerVisibility[] = baseSources.map((d: DataSourceRasterLayer) => {
                return {
                    layer_id: d.id,
                    layer_name: d.name,
                    is_visible: d.is_visible,
                    order: d.order,
                    source: d.source
                };
            });

            if (overlayVisibility.length === 0) {
                setOverlayVisible(baseLayers);
            }
            if (overlayVisibilityMapControl.length === 0) {
                setOverlayVisibleMapControl(overlayLayers);
            }

            setRasterDataSources(dataSources);
        }
    }, [dataSources, loading]);

    useEffect(() => {
        console.log("083123 overlayVisibilityMapControl", overlayVisibilityMapControl)
        console.log("083123 overlayVisibility", overlayVisibility)
        //let naip = overlayVisibility.find((item) => { return item.layer_id === NAIP_DATA_SOURCE });
        //updateLayersForExport(overlayVisibilityMapControl.concat(naip ? naip : []));
        updateLayersForExport(overlayVisibilityMapControl);
    }, [overlayVisibilityMapControl, overlayVisibility]);
    //
    //  END DATE / TIMELINE STUFF
    //  
    
    useEffect(() => {
        // After publishing: find and select the newly published zone set
        if (!updateToNewPublishedSet) return;
        const publishedIndex = zones?.findIndex((z: Zone) => z.zonesetId === recentPublishedZoneset) ?? -1;
        setUpdateToNewPublishedSet(false);
        if (publishedIndex >= 0) {
            setSelectedZoneset(publishedIndex);
            setSelectedZonesetStatus("published");
        }
    }, [zones, recentPublishedZoneset, updateToNewPublishedSet]);

    useEffect(() => {
        // After creating a prelim set: auto-select the most recent one
        if (!updateToNewPrelimSet) return;
        if (!zonesPrelim?.length) {
            setUpdateToNewPrelimSet(false);
            return;
        }
        const indexOfMostRecent = zonesPrelim.reduce((mostRecentIndex: number, element: Zone, index: number, array: Zone[]) => {
            const mostRecentDate = new Date((array[mostRecentIndex] as any).createdAt || 0);
            const currentDate = new Date((element as any).createdAt || 0);
            return currentDate > mostRecentDate ? index : mostRecentIndex;
        }, 0);
        setSelectedZoneset(indexOfMostRecent);
        setUpdateToNewPrelimSet(false);
    }, [zonesPrelim, updateToNewPrelimSet]);

    useEffect(() => {
        console.log("A041124 selectedZonesetStatus", selectedZonesetStatus);
    }, [selectedZonesetStatus]);

    useEffect(() => {
        if (!activeZoneset) {
            return;
        }

        const activePolygonSource: any = selectedZonesetStatus === "published" ? zonesDataSource : zonesPrelimDataSource;
        const activePointSource: any = selectedZonesetStatus === "published" ? zonesPointDataSource : zonesPrelimPointDataSource;

        console.log("ZONE_DEBUG activeZoneset", {
            status: selectedZonesetStatus,
            selectedZoneset,
            zonesetId: activeZoneset.zonesetId,
            name: activeZoneset.name,
            expectedPolygonCount: activeZoneset.geojson?.features?.length ?? activeZoneset.zones?.length ?? 0,
            polygonDataSourceCount: activePolygonSource?.features?.length ?? 0,
            pointDataSourceCount: activePointSource?.features?.length ?? 0,
            sourceKey: activeZoneSourceKey,
            polygonSourceId: activeZonePolygonSourceId,
            pointSourceId: activeZonePointSourceId,
        });
    }, [
        activeZonePointSourceId,
        activeZonePolygonSourceId,
        activeZoneSourceKey,
        activeZoneset,
        selectedZoneset,
        selectedZonesetStatus,
        zonesDataSource,
        zonesPointDataSource,
        zonesPrelimDataSource,
        zonesPrelimPointDataSource,
    ]);

    useEffect(() => {
        if (!map || !activeZoneset) {
            return;
        }

        const logRenderedZoneState = () => {
            const polygonSource: any = map.getSource(activeZonePolygonSourceId);
            const pointSource: any = map.getSource(activeZonePointSourceId);
            const renderedPolygonFeatures = map.queryRenderedFeatures(undefined, { layers: [activeZoneBorderLayerId] });
            const renderedLabelFeatures = map.queryRenderedFeatures(undefined, { layers: [activeZoneLabelLayerId] });
            const renderedPolygonLabels = Array.from(
                new Set(
                    renderedPolygonFeatures.map((feature: any) =>
                        feature.properties?.label ?? feature.properties?.value ?? feature.properties?.zone
                    ).filter(Boolean)
                )
            );
            const renderedLabelTexts = Array.from(
                new Set(
                    renderedLabelFeatures.map((feature: any) =>
                        feature.properties?.label ?? feature.properties?.value ?? feature.properties?.zone
                    ).filter(Boolean)
                )
            );

            console.log("ZONE_DEBUG renderedZones", {
                status: selectedZonesetStatus,
                selectedZoneset,
                zonesetId: activeZoneset.zonesetId,
                polygonSourceFeatureCount: polygonSource?._data?.features?.length ?? null,
                pointSourceFeatureCount: pointSource?._data?.features?.length ?? null,
                renderedPolygonFeatureCount: renderedPolygonFeatures.length,
                renderedPolygonUniqueLabels: renderedPolygonLabels,
                renderedPolygonUniqueCount: renderedPolygonLabels.length,
                renderedLabelFeatureCount: renderedLabelFeatures.length,
                renderedLabelTexts,
                renderedLabelUniqueCount: renderedLabelTexts.length,
            });
        };

        if (map.isStyleLoaded()) {
            window.setTimeout(logRenderedZoneState, 0);
            return;
        }

        map.once("idle", logRenderedZoneState);
        return () => {
            map.off("idle", logRenderedZoneState);
        };
    }, [
        activeZoneBorderLayerId,
        activeZoneLabelLayerId,
        activeZonePointSourceId,
        activeZonePolygonSourceId,
        activeZoneset,
        map,
        selectedZoneset,
        selectedZonesetStatus,
        zonesDataSource,
        zonesPointDataSource,
        zonesPrelimDataSource,
        zonesPrelimPointDataSource,
    ]);

    //console.log("080823 RASTER_DATA_SOURCES: ", RASTER_DATA_SOURCES);

    useEffect(() => {
        console.log("080823 rasterDataSources", rasterDataSources);
        if (rasterDataSources.length > 0) {
            /*let uas = rasterDataSources[0];
            let uasSource = {
                id: uas.id,
                layer_id: uas.id,
                layer_name: uas.name,
                is_visible: uas.is_visible,
                order: uas.order,
                uuid: nanoid(8),
                source:{
                    source: uas.source,
                    utility_layer: true,
                    stac_address: uas.source.stac_address,
                    version: 8,
                    sources: {
                        "raster-tiles": {
                            "type": "raster",
                            "tiles": uas.tiles,
                            "tileSize": 256,
                            "attribution": uas.source.citation
                        }
                    },
                    "layers": [{
                            "id": "uas-tiles",
                            "type": "raster",
                            "source": "raster-tiles",
                            "minzoom": 0,//(uas.source.max_zoom_level && uas.source.max_zoom_level < 25) ? uas.source.max_zoom_level : 24,
                            "maxzoom": 24,//(uas.source.min_zoom_level && uas.source.min_zoom_level < 25) ? uas.source.min_zoom_level : 0,
                        }
                    ]
                }
            }
            setOverlayVisible(prevVisibility => {
                let updateUAS = false;
                const updatedVisibility = prevVisibility.map(layer => {
                    if (layer.layer_name === "UAS Imagery") {
                        updateUAS = true;
                        return uasSource;                        
                    }
                    return layer;
                });
                return updateUAS ? updatedVisibility : [...updatedVisibility, uasSource];
            });*/
            setOverlayVisible(prevVisibility => [...prevVisibility]);
        }
    }, [rasterDataSources]);

    const updateRasterWithNoTimeline = (_layer: any,_datesAvailable:any ) => {
        console.log("080923 _layer", _layer);
        console.log("080923 overlayVisibility", overlayVisibility);
        console.log("080923 _datesAvailable", _datesAvailable);
        let _tiles = getTileUrlTemplate(
            _layer.source.source,
            _layer.source.stac_address!, 
            _layer.source.source.collection_id!,
            'footprint_id',
            "-01-01",
            _datesAvailable[0],
            _layer.source.source.date_display_resolution!,
            "template-singledate-p1",
            null,
        ) 
        console.log("080923 _tiles", _tiles);
        setOverlayVisible((prevVisibility:LayerVisibility[]) => {
            let _oldVisibility = prevVisibility.filter((v:LayerVisibility) => v.layer_id != _layer.id);
            let _newVisibility = prevVisibility.filter((v:LayerVisibility) => v.layer_id === _layer.id);
            console.log("080923 _newVisibility", _newVisibility);
            _newVisibility[0].source.sources["raster-tiles"].tiles = _tiles;
            _newVisibility[0]["uuid"] = nanoid(8);
            let _returnable = [..._oldVisibility, _newVisibility[0]] as LayerVisibility[];
            console.log("080923 _returnable", _returnable);
            console.log("080923 prevVisibility", prevVisibility);
            return _returnable;
        });
    }
    
    useEffect(() => {
        console.log("A040424 fieldData", fieldData);
        console.log("A040424 field", field);
        if(fieldData) {
            const [fields, featureCollection, featureCollectionSelected] = createCollectionFromFields(fieldData, field);
            setFieldsDataSource(featureCollection);
            setFieldsDataSourceSelected(featureCollectionSelected);
            setFields(fields);
        }
    }, [fieldData, field]);
    
    useEffect(() => {
        console.log("040324 zone", zone);
        if(zone){
            console.log("Z040624 B");
            setSelectedZoneName(zone);
        }
    }, [zone]);

    useEffect(() => {
        console.log("040324 field", field);
        console.log("040324 fields", fields);
        if(field && fields) {
            const editingField = fields.find(f => f.id == field);

            if(editingField) {
                setEditField({...editingField});

                // editor needs an array of polygon features
                const fieldFeature = createPolygonFeatureFromField(editingField);
                setEditFeature([fieldFeature]);

                // override the viewport and zoom to the field
                if(width && height) {
                    setViewport(zoomToFeature(fieldFeature, viewport, width, height, 100));
                }

                setMode(new EditingMode());
            }
        }
    }, [field, fields, width, height]);

    const createCollectionFromFields = (fieldData: GetFields, fieldId: string) => {
        const fields = fieldData.landscan_fields.map((f:any) => {
            return {...f, id: f.field_id } as Field;
        });

        // create features
        const features = fields.map((f:Field) => createFeatureFromField(f));

        // create a collection, exclude the edit field if one exists
        const featureCollection = createFeatureCollection(field ? 
            features.filter((f: any) => f.properties.id != field) : features);

        const featureCollectionSelected = createFeatureCollection(field ? 
            features.filter((f: any) => f.properties.id == field) : features);

        return [fields, featureCollection, featureCollectionSelected];
    }

    const onSelect = useCallback(options => {
        setSelectedFeatureIndex(options && options.selectedFeatureIndex);

        if (options && options.selectedFeatureIndex && options.selectedFeatureIndex >= 0) {
            setMode(new EditingMode());
        }
    }, []);

    const onUpdate = useCallback((event) => {
        const { editType, data } = event;
        
        setEditFeature(data);

        console.log(editType);

        if (editType === 'addFeature') {
            setMode(new EditingMode());
        }
    }, []);

    const getCursor = () => {
        return 'pointer';
    }
    
    const delayedUrlUpdate = React.useCallback(_.debounce((url:any) => history.replace(url), 250), []);

    const onInteractionStateChange = (e: any) => {
        if(!e.isDragging && !e.isPanning && !e.isZooming) {
            //const str = `@${viewport.latitude.toFixed(5)},${viewport.longitude.toFixed(5)},${viewport.zoom.toFixed(2)}`;
            //const url = generatePath(`${FIELD_APP_URL}/:vp/fields/:zone/editzone`, { vp: str, zone: "editField.id" })
            console.log("040324 editField", editField);
            //const url = editField ? 
                //generatePath(`${FIELD_APP_URL}/:vp/fields/:zone/editzone`, { vp: str, zone: editField.id }) : 
                //generatePath(`${FIELD_APP_URL}/:vp/fields/new`, {vp: str});
    
            //delayedUrlUpdate(url);
        }
    }

    const deletePrelimZone = async (index:number) => {
        console.log("D040624 DELETE", index);
        console.log("D040624 DELETE", zonesPrelim ? zonesPrelim[index] : null);
        if (zonesPrelim && zonesPrelim[index] && zonesPrelim[index].zonesetId !== undefined){
            const result = await LandscanService.deleteZoneSetPrelim(zonesPrelim[index].zonesetId as string);
            console.log("D040624 result", result);
            //setRecentPublishedZoneset(result.zonesetId as string);
            setUpdateZonesets((prev:number) => {
                return prev + 1;
            })
        }
    }
    const publishPrelimZone = async (index:number) => {
        console.log("D040624 PUBLISH", index);
        console.log("D040624 PUBLISH", zonesPrelim ? zonesPrelim[index] : null);
        if (zonesPrelim && zonesPrelim[index] && zonesPrelim[index].zonesetId !== undefined){
            //setJobStatus(`Publishing: ${zonesPrelim[index].name}`)
            setPublishingZone(index);
            //setJobRunning(true);
            //setJobPercent
            const result = await LandscanService.publishZoneSet(zonesPrelim[index].zonesetId as string);
            console.log("D040624 result", result);
            //setJobStatus(`no jobs running`)
            setPublishingZone(-99);
            setRecentPublishedZoneset(result.zonesetId as string);
            //setSelectedZoneset(index);
            //setSelectedZonesetStatus("published");
            setUpdateToNewPublishedSet(true);
            setUpdateZonesets((prev:number) => {
                return prev + 1;
            })
        }
    }
    const unpublishZone = async (index:number) => {
        console.log("D040624 UNPUBLISH", index);
        console.log("D040624 UNPUBLISH", zones ? zones[index] : null);
        if (zones && zones[index] && zones[index].zonesetId !== undefined){
            //setJobStatus(`Un-publishing: ${zones[index].name}`)
            setUnPublishingZone(index);
            const result = await LandscanService.unpublishZoneSet(zones[index].zonesetId as string);
            console.log("D040624 result", result);
            //setJobStatus(`no jobs running`)
            setUnPublishingZone(-99);
            //setRecentPublishedZoneset(result.zonesetId as string);
            //setSelectedZoneset(index);
            //setSelectedZonesetStatus("published");
            //setUpdateToNewPublishedSet(true);
            setUpdateZonesets((prev:number) => {
                return prev + 1;
            })
        }
    }

    const zoneInfoChildren = useMemo(() => {

        if (!zones) {
            return;
        }

        return (
                <>                
                <LayerVisibilityControlPanel 
                    showDatePicker={true} 
                    layers={overlayVisibilityMapControl} 
                    onChange={(visibility: LayerVisibility[]) =>  setOverlayVisibleMapControl(visibility)} 
                    context={"sidebar"}
                    dateSelectorParameters={dateSelectorParameters}
                    setDateSelectorParameters={setDateSelectorParameters} 
                    dateIndexSelected={dateIndexSelected}
                    setDateIndexSelected={setDateIndexSelected}
                />
                <div className="d-flex flex-column">
                    <h4>Zone Sets</h4>
                </div>
                <div className="field-list">
                {console.log("A061324 selectedZoneset", selectedZoneset)}
                {console.log("A061324 selectedZonesetStatus", selectedZonesetStatus)}
                {zones.map((_zone: any, index:number) => (
                    <div key={_zone.zonesetId} 
                        className="d-flex flex-row list-group-item list-group-item-light list-group-item-action"
                        style={{
                            cursor: 'pointer',
                            backgroundColor: (index === selectedZoneset) && (selectedZonesetStatus === "published") ?  "#e6e6e6" : '#fdfdfe',
                            justifyContent:"space-between",
                        }}    
                        onClick={() => {
                            setSelectedZoneset(index);
                            setSelectedZonesetStatus("published");
                        }}
                    ><div>
                        <div
                            style={{
                                fontSize:".875rem",
                                fontWeight: "bold",
                            }}
                        >{_zone.name} <span style={{color:"red"}}>{recentPublishedZoneset === _zone.zonesetId ? "(recently published)" : ""}</span></div>
                        <div
                            style={{
                                fontSize:"10px",
                            }}
                        >{`Number of Zones: ${_zone.zones?.length}`}</div>
                        <div
                            style={{
                                fontSize:"10px",
                            }}
                        >{`Created: ${_zone.createdAt}`}</div>
                        <div
                            style={{
                                fontSize:"10px",
                            }}
                        >{`Description: ${_zone.description}`}</div>
                        <div
                            style={{
                                fontSize:"10px",
                            }}
                        >{`Entity Id: ${_zone.entityId}`}</div>
                       </div>
                       <div className="d-flex flex-column" style={{ justifyContent:"space-evenly" }}>
                        { unPublishingZone !== index
                        ?   <Button className="rounded-circle" variant="light" size="sm" title="Unpublish this zoneset" 
                                onClick={(event:any) => {
                                    event.stopPropagation()
                                    unpublishZone(index)
                                    
                                }} 
                                style={{cursor:'pointer', height:'30px', width:'30px'}}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        :   <Button className="rounded-circle" variant="light" size="sm" title="Unpublish in progress" 
                                onClick={(event:any) => {
                                    event.stopPropagation()
                                    console.log("I'm busy un-publishing!")
                                }} 
                                style={{cursor:'pointer', height:'30px', width:'30px'}}>
                                <Spinner style={{
                                    width: '12.5px',
                                    height: '12.5px',
                                    color: '#212529 !important',
                                }} animation="border" />
                            </Button>
                        }
                    </div>
                    </div>
                ))  
                }
                </div>
                <div 
                    className="d-flex flex-column"
                    style={{
                        paddingTop: "10px",
                    }}>
                    <h4>Preliminary Zone Sets</h4>
                </div>
                <div className="field-list">
                    <div 
                        className="d-flex flex-column list-group-item list-group-item-light"
                        style={{
                            paddingTop: "10px",
                            paddingBottom: jobRunning == "" ? "0px" : "10px",
                        }}>
                        {jobRunning !== "" && jobStatus !== "failed"
                        ? <div style={{ width:"100%", textAlign:"center", paddingBottom:"10px"}}>
                            <LoadingSpinnerSmallTextInside message={`${ jobPercent * 100 }%`} messageLocation={"inside"}></LoadingSpinnerSmallTextInside>
                        </div>
                        : <></>}
                        <h6>{jobRunning == "" ? jobStatus : `${jobRunning}: ${jobStatus}`}</h6>
                    </div>
                    {zonesPrelim ? zonesPrelim.map((_zone: any, index:number) => (
                        console.log("C040624 _zone", _zone),
                        <div key={_zone.zonesetId} 
                            className="d-flex flex-row list-group-item list-group-item-light list-group-item-action"
                            style={{
                                cursor: 'pointer',
                                backgroundColor: ((index === selectedZoneset) && (selectedZonesetStatus === "prelim")) ?  "#e6e6e6" : '#fdfdfe',
                                justifyContent:"space-between",
                            }}    
                            onClick={() => {
                                setSelectedZoneset(index);
                                setSelectedZonesetStatus("prelim");
                            }}
                        ><div>
                            <div
                                style={{
                                    fontSize:".875rem",
                                    fontWeight: "bold",
                                }}
                            >{_zone.name}</div>
                            <div
                                style={{
                                    fontSize:"10px",
                                }}
                            >{`Number of Zones: ${_zone.geojson?.features?.length}`}</div>
                            <div
                                style={{
                                    fontSize:"10px",
                                }}
                            >{`Created: ${_zone.createdAt}`}</div>
                            <div
                                style={{
                                    fontSize:"10px",
                                }}
                            >{`Description: ${_zone.description}`}</div>
                            <div
                                style={{
                                    fontSize:"10px",
                                }}
                            >{`Entity Id: ${_zone.entityId}`}</div>
                            </div>
                            <div className="d-flex flex-column" style={{ justifyContent:"space-evenly" }}>
                            <Button className="rounded-circle" variant="light" size="sm" title="Delete this zoneset" 
                                onClick={(event:any) => {
                                    event.stopPropagation()
                                    deletePrelimZone(index)
                                }} 
                                style={{cursor:'pointer', height:'30px', width:'30px'}}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                            {publishingZone !== index
                            ?   <Button className="rounded-circle" variant="light" size="sm" title="Publish this zoneset" 
                                    onClick={(event:any) => {
                                        event.stopPropagation()
                                        publishPrelimZone(index)
                                    }} 
                                    style={{cursor:'pointer', height:'30px', width:'30px'}}>
                                    
                                    <FontAwesomeIcon icon={faCheck} />
                                </Button>
                            :   <Button className="rounded-circle" variant="light" size="sm" title="Publish in progress" 
                                    onClick={(event:any) => {
                                        event.stopPropagation()
                                        console.log("I'm busy publishing!")
                                    }} 
                                    style={{cursor:'pointer', height:'30px', width:'30px'}}>
                                    <Spinner style={{
                                        width: '12.5px',
                                        height: '12.5px',
                                        color: '#212529 !important',
                                    }} animation="border" />
                                </Button>
                            }
                            </div>
                        </div>
                    ))  
                    : null }
                </div>
            </>
        )
    }, [zone, field, zonesDataSource, zonesPointDataSource, zonesPrelimDataSource, zonesPrelimPointDataSource, 
        jobStatus, jobRunning,zonesPrelim, zones, jobPercent, selectedZoneset, selectedZonesetStatus, overlayVisibilityMapControl, unPublishingZone, publishingZone]);

    const setShowDownload = (show: boolean) => {
        console.log("040324 setShowDownload", show);
    }
    const onInfoDrawerClose = () => {
        console.log("040324 onInfoDrawerClose");
    }

    return (
        <div className="d-flex h-100 position-relative">
            <div className="bg-light border border-bottom-0 border-dark border-top-0 d-flex flex-column flex-shrink-0 shadow-sm text-dark overflow-auto" style={{ width: '300px' }}>
               <EditZoneForm 
                    fieldToEdit={editField}
                    field={field}
                    zoneToEdit={zone}
                    feature={editFeature}
                    editField={editField}
                    setJobStatus={setJobStatus}
                    setJobRunning={setJobRunning}
                    setJobPercent={setJobPercent}
                    setUpdateZonesets={setUpdateZonesets}
                    setSelectedZonesetStatus={setSelectedZonesetStatus}
                    setUpdateToNewPrelimSet={setUpdateToNewPrelimSet}
                />

            </div>
            {field &&
                <div className="bg-light border border-bottom-0 border-left-0 border-right border-secondary border-top-0 d-flex flex-column flex-shrink-0 rounded-right text-dark" style={{ width: '295px' }}>
                    <ZoneInfoDrawer 
                        zone={{name: zone}} 
                        onClose={onInfoDrawerClose}
                        setShowDownload={setShowDownload}
                        showClose={false}
                        showDownload={false}
                        field={field}
                        setZones={setZones}
                        setZonesPrelim={setZonesPrelim}
                        updateZonesets={updateZonesets}
                    >
                        {zoneInfoChildren}
                    </ZoneInfoDrawer>
                </div>
            }
            <div className="flex-fill bg-white position-relative" ref={mapContainerRef}>
                <MapGL
                    {...viewport}
                    width='100%'
                    height='100%'
                    getCursor={() => getCursor()}
                    doubleClickZoom={false}
                    mapStyle={ESRI_BASE}
                    onInteractionStateChange={(s:any) => onInteractionStateChange(s)} 
                    onViewportChange={(v:ViewState) => setViewport(v)}
                    ref={(ref:any) => ref && setMap(ref.getMap())}
                    >    
                    {
                        ['baselayer', 'fieldlayer', 'zoneslayer', 'labellayer'].map(name => {
                            return (<Layer
                                key={name}
                                id={'GROUP_' + name}
                                type='background'
                                layout={{ visibility: 'none' }}
                                paint={{}}
                            />)
                        })
                    }
                    <Source type="geojson" data={fieldsDataSourceSelected}>
                        {/*<Layer {...editFieldsLayer} />*/}
                        <Layer 
                            {...fieldsBorderLayerSelected} 
                            beforeId="GROUP_zoneslayer"
                        />
                    </Source>              
                    <Source
                        key={`zones-polygon-source-${activeZoneSourceKey}`}
                        type="geojson"
                        data={selectedZonesetStatus === "published" ? zonesDataSource : zonesPrelimDataSource}
                        id={activeZonePolygonSourceId}
                    >
                        <Layer beforeId="GROUP_zoneslayer" id={activeZoneBorderLayerId} {...selectedZonesetStatus === "published" ? {...zonesBorderLayerEdit} : {...zonesPrelimBorderLayer}} minzoom={10} />
                        <Layer id={activeZoneFillLayerId} beforeId={activeZoneBorderLayerId} {...selectedZonesetStatus === "published" ? {...zonesLayer} : {...zonesPrelimLayer}} minzoom={10} />
                    </Source>
                    <Source
                        key={`zones-point-source-${activeZoneSourceKey}`}
                        type="geojson"
                        data={selectedZonesetStatus === "published" ? zonesPointDataSource : zonesPrelimPointDataSource}
                        id={activeZonePointSourceId}
                    >
                        {/* Circle layer for background */}
                        <Layer
                            id={activeZoneCircleLayerId}
                            //beforeId="zones-label" 
                            beforeId="GROUP_labellayer"
                            type="circle"
                            source={activeZonePointSourceId}
                            paint={{
                                "circle-radius": 15, 
                                "circle-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName], 
                                    selectedZonesetStatus === "published" ? '#000' : '#fff',
                                    selectedZonesetStatus === "published" ? '#fff' : '#000', 
                                  ], 
                                "circle-opacity": 0.75,
                                "circle-stroke-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName],
                                    '#fff',
                                    selectedZonesetStatus === "published" ? '#000' : '#fff',
                                  ], 
                                "circle-stroke-width": 1,
                            }}
                        />
                        <Layer
                            type="symbol"
                            id={activeZoneLabelLayerId}
                            beforeId="GROUP_labellayer"
                            source={activeZonePointSourceId}
                            layout={{
                                "text-field": ["get", "label"], 
                                "text-font": ["Open Sans Regular","Arial Unicode MS Regular"], 
                                "text-size": 16, 
                                "text-anchor": "center", 
                                }}
                            paint={{
                               "text-color": [
                                    'case',
                                    ['==', ['get', 'label'], selectedZoneName], 
                                    '#fff',
                                    selectedZonesetStatus === "published" ? '#000' : '#fff', 
                                ],  
                               //"text-halo-color": "#000", 
                               //"text-halo-width": 1, 
                            }}
                            minzoom={10} // Adjust zoom levels as necessary
                        />
                    </Source>
                    {

                    overlayVisibility.map((layer: LayerVisibility) => (
                        console.log("A050524 overlayVisibility > layer", layer),
                        <Source 
                            key={`s-${layer.layer_id}`}
                            id={`s-${layer.layer_id}`}
                            type='raster'
                            tileSize={256}
                            tiles={layer.source.sources['raster-tiles'].tiles}>
                            <Layer 
                                key={layer.layer_id} 
                                id={layer.layer_id} 
                                type='raster' 
                                //beforeId="zones-polygon-bnd"
                                beforeId="GROUP_baselayer"
                                source={`s-${layer.layer_id}`} 
                                maxzoom={layer.source.layers[0].maxzoom} 
                                minzoom={layer.source.layers[0].minzoom} 
                                layout={{ visibility: layer.is_visible ? 'visible' : 'none' }} 
                            />
                        </Source>
                        ))
                    }

                    {rasterDataSources ? _.orderBy(rasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer) => (
                        console.log("C041224 rasterDataSources > s", s),
                        <Source key={s.uuid} id={s.uuid} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={s.uuid}
                                //beforeId="zones-polygon-bnd"
                                beforeId="GROUP_baselayer"
                                id={s.uuid}
                                type='raster'
                                source={s.uuid}
                                maxzoom={s.max_zoom} minzoom={s.min_zoom}
                                layout={{ visibility: ((s.type === "overlay") && (activeAnimation === "playing" || activeAnimation === "paused") && loadedTiles) 
                                                ? 'none' 
                                                : overlayVisibility.concat(overlayVisibilityMapControl).find((item) => { return item.layer_id === s.id })?.is_visible 
                                                    ? 'visible' 
                                                    : 'none'
                                        }}
                            />
                        </Source>)
                    ) : null}

                    {animationRasterDataSources ? _.orderBy(animationRasterDataSources, ['order'], ['desc']).map((s: DataSourceRasterLayer, index:number) => (
                        console.log("C041224 animationRasterDataSources > s", s),
                        <Source key={`${s.uuid}`} id={`${s.uuid}`} type='raster' tileSize={256} tiles={s.tiles}>
                            <Layer
                                key={`${s.uuid}`}
                                //beforeId="zones-polygon-bnd"
                                beforeId="GROUP_baselayer"
                                id={`${s.uuid}`}
                                type='raster'
                                source={s.uuid}
                                maxzoom={s.max_zoom}
                                minzoom={s.min_zoom}
                                layout={{ visibility: (activeAnimation === "playing" || activeAnimation === "paused")
                                        ? 'visible'
                                        : 'none'
                                    }}
                                paint={{
                                    'raster-opacity': s.opacity,
                                }}
                            />
                        </Source>)
                    ) : null}
                    {console.log("050624 fieldsDataSource", fieldsDataSource)}
                    {console.log("050624 fieldsDataSourceSelected", fieldsDataSourceSelected)}
                    <Source type="geojson" data={fieldsDataSource}>
                        {/*<Layer {...editFieldsLayer} />*/}
                        <Layer 
                            {...fieldsBorderLayer} 
                            beforeId="GROUP_zoneslayer"    
                        />
                    </Source>     

                    {/*<Editor
                        ref={editorRef}
                        style={{ width: '100%', height: '100%' }}
                        clickRadius={12}
                        mode={mode}
                        onUpdate={onUpdate}
                        onSelect={onSelect}
                        features={editFeature}
                        editHandleShape={'circle'}
                        featureStyle={getFeatureStyle}
                        editHandleStyle={getEditHandleStyle}
                    />*/}
                    <NavigationControl style={navControlStyle} showCompass={false} />
                </MapGL>
                <div style={{position: 'absolute', bottom:'unset', top:'10px', right:'10px'}}>
                    <LayerVisibilityControlPanel
                        showDatePicker={false}
                        layers={overlayVisibility}
                        onChange={(visibility: LayerVisibility[]) => setOverlayVisible(visibility)}
                        updateRasterWithNoTimeline={updateRasterWithNoTimeline}
                     />
                </div>
                <div style={{
                    width:'100%', 
                    position: 'relative', 
                    top:'unset', 
                    bottom:'110px', 
                    right:'0px', 
                    height: '110px', 
                    display:"flex",
                    pointerEvents:'none',
                }}>
                    <LayerVisibilityControlPanel 
                        showDatePicker={true} 
                        layers={overlayVisibilityMapControl} 
                        onChange={(visibility: LayerVisibility[]) => setOverlayVisibleMapControl(visibility)} 
                        context={"timeline"}
                        dateSelectorParameters={dateSelectorParameters}
                        setDateSelectorParameters={setDateSelectorParameters} 
                        dateIndexSelected={dateIndexSelected}
                        setDateIndexSelected={setDateIndexSelected}
                        loadAnimationTiles={loadAnimationTiles}
                        loadedTiles={loadedTiles}
                        timelineStatus={timelineStatus}
                        setTimelineStatus={setTimelineStatus}
                        activeAnimation={activeAnimation}
                        setActiveAnimationFromParent={setActiveAnimationFromParent}
                        indexA={indexA}
                        setIndexA={setIndexA}
                    />       
                    
                </div>
            </div>
        </div>
    );
}
